import { useMemo } from "react";
import { useReservationContext } from "@/contexts/ReservationContext";
import { matchesGuestStatusFilter } from "@/components/pages/reservations/guests/utils/guestStatusUtils";
import { GUEST_STATUS_FILTER_DEFAULT } from "@/components/pages/reservations/utils/reservationGuestStatusFilters";

/**
 * Custom hook that provides easy access to reservation filtering functionality
 * This demonstrates how other components can use the centralized filtering
 */
export const useReservationFiltering = () => {
  const { filters, actions, computed, useReservations } =
    useReservationContext();

  // Get filtered reservations using the context filters
  const reservationsQuery = useReservations(
    filters.currentPage,
    computed.limit,
    computed.apiFilters,
  );

  // Extract data
  const reservationsData = reservationsQuery.data;
  const allReservations = reservationsData?.data || [];

  const reservations = useMemo(() => {
    if (filters.guestStatusFilter === GUEST_STATUS_FILTER_DEFAULT) {
      return allReservations;
    }

    return allReservations.filter((reservation) =>
      matchesGuestStatusFilter(reservation, filters.guestStatusFilter),
    );
  }, [allReservations, filters.guestStatusFilter]);

  const totalPages = Math.ceil((reservationsData?.total || 0) / computed.limit);

  return {
    // Data
    reservations,
    // stats,
    totalPages,
    isLoading: reservationsQuery.isLoading,
    error: reservationsQuery.error,

    // Filter state
    filters,

    // Actions
    actions,

    // Computed values
    computed,

    // Pagination helpers
    pagination: {
      currentPage: filters.currentPage,
      totalPages,
      hasNextPage: filters.currentPage < totalPages,
      hasPrevPage: filters.currentPage > 1,
      goToNextPage: () => {
        if (filters.currentPage < totalPages) {
          actions.setCurrentPage(filters.currentPage + 1);
        }
      },
      goToPrevPage: () => {
        if (filters.currentPage > 1) {
          actions.setCurrentPage(filters.currentPage - 1);
        }
      },
      goToPage: (page: number) => {
        if (page >= 1 && page <= totalPages) {
          actions.setCurrentPage(page);
        }
      },
    },

    // Selection helpers
    selection: {
      selectedReservations: filters.selectedReservations,
      isAllSelected:
        filters.selectedReservations.length === reservations.length &&
        reservations.length > 0,
      selectedCount: filters.selectedReservations.length,
      selectReservation: actions.handleSelectReservation,
      selectAll: (checked: boolean) =>
        actions.handleSelectAll(
          checked,
          reservations.map((r) => r.id),
        ),
      clearSelection: () => actions.setSelectedReservations([]),
    },
  };
};

/**
 * Hook for getting reservations with specific overrides while maintaining context state
 * Useful when you need to temporarily modify filters without affecting global state
 */
export const useReservationsWithOverrides = (
  overrides: Partial<typeof useReservationContext>["computed"]["apiFilters"],
) => {
  const { computed, useReservations } = useReservationContext();

  const mergedFilters = {
    ...computed.apiFilters,
    ...overrides,
  };

  return useReservations(1, computed.limit, mergedFilters);
};
